'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/browser';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Save, X as CloseIcon } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { UsernameInput, DisplayNameInput } from '@/components/ui/UsernameInput';
import { AudioReelPlayer } from './AudioReelPlayer';
import { VideoReelPlayer } from './VideoReelPlayer';
import {
  storageUploadWithProgress,
  preflightReelFile,
  describeUploadError,
  reportUploadError,
  StorageUploadError,
  type ReelKind,
} from '@/lib/upload/storageUploadXhr';
import { cn } from '@/lib/utils';
// ds primitives for the Talent-details section (the older sections in this file
// predate the ds set and use @/components/ui/*; aliased to avoid name clashes).
import {
  Input as DsInput,
  Select as DsSelect,
  Switch as DsSwitch,
  Label as DsLabel,
} from '@/components/ds';

const VOICE_TYPE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['masculine', 'Masculine'],
  ['feminine', 'Feminine'],
  ['androgynous', 'Androgynous'],
  ['neutral', 'Neutral'],
];
const UNION_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['union', 'Union'],
  ['non-union', 'Non-union'],
];
const RATE_UNIT_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['per_hour', 'per hour'],
  ['per_project', 'per project'],
  ['per_word', 'per word'],
  ['per_finished_minute', 'per finished minute'],
];

function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ProfileSectionEditorProps {
  sectionId: string;
  userId: string;
  currentData: any;
  onSave: (data: any) => void;
  onClose: () => void;
  onRefresh?: () => void;
}

// The columns each section is allowed to write to `profiles`. The save path
// picks from formData by this list rather than spreading it: formData is seeded
// from caller-supplied currentData, and PostgREST rejects the *entire* statement
// if it sees one key that isn't a column — which would break the whole section
// with a generic "failed to update" toast. An allowlist keeps a stray key from
// ever reaching the wire.
//
// `talent-details` and `demo-reels` are absent because they build their payloads
// explicitly in handleSave. The contact section edits a `phone` field that has no
// column on profiles; it is deliberately not listed, so it is not written.
const SECTION_FIELDS: Record<string, readonly string[]> = {
  about: ['full_name', 'display_name', 'username', 'bio', 'voice_tagline', 'location'],
  skills: ['skills', 'specialties'],
  avatar: ['avatar_url'],
  background: ['banner_url'],
  resume: ['resume_url'],
  contact: ['email'],
  photos: [],
};

// Pending demo-reel attachment — uploaded to storage but not yet committed
// to media_files. Either gets confirmed on Save Changes or rolled back
// (storage delete) on Cancel/Remove/Replace.
type PendingReel = {
  storagePath: string;
  publicUrl: string;
  file: File;
};

export default function ProfileSectionEditor({
  sectionId,
  userId,
  currentData,
  onSave,
  onClose,
  onRefresh
}: ProfileSectionEditorProps) {
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [customSkill, setCustomSkill] = useState('');
  const [customSpecialty, setCustomSpecialty] = useState('');
  // Rate is edited in dollars but stored as integer cents (min_rate_cents).
  // Kept out of formData so the dollar amount can't be mistaken for a column
  // value; talent-details builds its payload explicitly below.
  const [rateAmount, setRateAmount] = useState('');
  const [pendingAudio, setPendingAudio] = useState<PendingReel | null>(null);
  const [pendingVideo, setPendingVideo] = useState<PendingReel | null>(null);
  const [audioBusy, setAudioBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const audioAbortRef = useRef<AbortController | null>(null);
  const videoAbortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    setFormData(currentData || {});
  }, [currentData]);

  // Talent-details load: cents -> dollars so editing is non-destructive.
  useEffect(() => {
    if (sectionId !== 'talent-details') return;
    const cents = currentData?.min_rate_cents;
    setRateAmount(
      typeof cents === 'number' && cents >= 0 ? String(cents / 100) : ''
    );
  }, [currentData, sectionId]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Cancel = close without committing. Pending uploads land in storage
  // immediately (so the preview can play); without this cleanup they'd
  // become orphaned objects nobody references. Best-effort delete; if it
  // fails the orphan still exists but that's strictly better than a hung
  // close.
  const handleCancel = async () => {
    const orphans = [pendingAudio, pendingVideo].filter(Boolean) as PendingReel[];
    if (orphans.length > 0) {
      try {
        const supabase = createClient();
        await supabase.storage
          .from('user-assets')
          .remove(orphans.map((o) => o.storagePath));
      } catch (err) {
        console.warn('Failed to clean orphan storage objects on cancel:', err);
      }
    }
    onClose();
  };

  const commitPendingReel = async (
    pending: PendingReel,
    reelType: 'audio' | 'video',
    supabase: ReturnType<typeof createClient>
  ) => {
    const { error: dbError } = await supabase.from('media_files').insert({
      user_id: userId,
      file_type: reelType,
      file_url: pending.storagePath,
      file_name: pending.file.name,
      file_size: pending.file.size,
      mime_type: pending.file.type,
      label: `${reelType === 'audio' ? 'Audio' : 'Video'} Demo Reel`,
      visibility: 'public',
    });
    if (dbError) {
      console.error('Error committing reel metadata:', dbError);
      throw dbError;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const supabase = createClient();

      // For demo-reels, commit any pending attachments to media_files.
      // Other sections write to profiles directly.
      if (sectionId === 'demo-reels') {
        if (pendingAudio) {
          await commitPendingReel(pendingAudio, 'audio', supabase);
        }
        if (pendingVideo) {
          await commitPendingReel(pendingVideo, 'video', supabase);
        }
        setPendingAudio(null);
        setPendingVideo(null);
        toast({
          title: 'Demo reels saved',
          description:
            (pendingAudio || pendingVideo)
              ? 'Your demo reels have been added to your profile.'
              : 'No changes to save.',
        });
        onSave({});
        onRefresh?.();
        onClose();
        return;
      }

      // Talent details: validate client-side (the DB CHECKs would otherwise
      // reject) and convert the dollar rate to integer cents before writing.
      if (sectionId === 'talent-details') {
        const parseAge = (v: any): number | null => {
          if (v === '' || v === null || v === undefined) return null;
          const n = Number(v);
          return Number.isFinite(n) ? Math.trunc(n) : NaN;
        };
        const minAge = parseAge(formData.voice_age_min);
        const maxAge = parseAge(formData.voice_age_max);
        const ageOutOfRange = (a: number | null) =>
          a !== null && (Number.isNaN(a) || a < 0 || a > 120);
        if (ageOutOfRange(minAge) || ageOutOfRange(maxAge)) {
          toast({
            title: 'Invalid age range',
            description: 'Castable ages must be whole numbers between 0 and 120.',
            variant: 'destructive',
          });
          return;
        }
        if (minAge !== null && maxAge !== null && minAge > maxAge) {
          toast({
            title: 'Invalid age range',
            description: 'Minimum castable age must be less than or equal to the maximum.',
            variant: 'destructive',
          });
          return;
        }

        const amountStr = rateAmount.trim();
        let minRateCents: number | null = null;
        if (amountStr !== '') {
          const dollars = Number(amountStr);
          if (!Number.isFinite(dollars) || dollars < 0) {
            toast({
              title: 'Invalid rate',
              description: 'Enter a minimum rate of 0 or more, or leave it blank.',
              variant: 'destructive',
            });
            return;
          }
          minRateCents = Math.round(dollars * 100);
        }

        const talentPayload = {
          voice_age_min: minAge,
          voice_age_max: maxAge,
          voice_type: Array.isArray(formData.voice_type) ? formData.voice_type : [],
          union_status: Array.isArray(formData.union_status) ? formData.union_status : [],
          open_to_unpaid: !!formData.open_to_unpaid,
          min_rate_cents: minRateCents,
          // Blank amount => null cents AND null unit; otherwise default the unit.
          rate_unit: minRateCents === null ? null : formData.rate_unit || 'per_hour',
        };

        const { error: talentError } = await supabase
          .from('profiles')
          .update(talentPayload)
          .eq('id', userId);
        if (talentError) throw talentError;

        onSave(talentPayload);
        toast({
          title: 'Profile Updated',
          description: 'Your talent details have been saved.',
        });
        onClose();
        return;
      }

      // Update the profile with the new data, narrowed to this section's columns.
      const allowed = SECTION_FIELDS[sectionId] ?? [];
      const payload: Record<string, any> = {};
      for (const key of allowed) {
        if (key in formData) payload[key] = formData[key];
      }

      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', userId);

      if (error) throw error;

      onSave(payload);
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addCustomSkill = () => {
    if (customSkill.trim() && !formData.skills?.includes(customSkill.trim())) {
      const newSkills = [...(formData.skills || []), customSkill.trim()];
      handleInputChange('skills', newSkills);
      setCustomSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    const newSkills = formData.skills?.filter((skill: string) => skill !== skillToRemove) || [];
    handleInputChange('skills', newSkills);
  };

  const addCustomSpecialty = () => {
    if (customSpecialty.trim() && !formData.specialties?.includes(customSpecialty.trim())) {
      const next = [...(formData.specialties || []), customSpecialty.trim()];
      handleInputChange('specialties', next);
      setCustomSpecialty('');
    }
  };

  const removeSpecialty = (specialtyToRemove: string) => {
    const next = formData.specialties?.filter((s: string) => s !== specialtyToRemove) || [];
    handleInputChange('specialties', next);
  };

  // Toggle a value in/out of a text[] form field (voice_type, union_status).
  const toggleArrayValue = (field: string, value: string) => {
    setFormData((prev: any) => {
      const arr: string[] = Array.isArray(prev[field]) ? prev[field] : [];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const renderSectionContent = () => {
    switch (sectionId) {
      case 'about':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name" className="text-text-primary">Full Name</Label>
              <Input
                id="full_name"
                value={formData.full_name || ''}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                placeholder="Enter your full name"
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
            <DisplayNameInput
              value={formData.display_name || ''}
              onChange={(value) => handleInputChange('display_name', value)}
              label="Display Name"
              placeholder="Enter your display name"
              className="[&_input]:bg-surface-3 [&_input]:text-text-primary [&_input]:border-border-default [&_input]:placeholder:text-text-tertiary [&_input]:focus:border-accent [&_label]:text-text-primary"
            />
            <UsernameInput
              value={formData.username || ''}
              onChange={(value) => handleInputChange('username', value)}
              label="Username"
              placeholder="Enter your username"
              checkAvailability={true}
              className="[&_input]:bg-surface-3 [&_input]:text-text-primary [&_input]:border-border-default [&_input]:placeholder:text-text-tertiary [&_input]:focus:border-accent [&_label]:text-text-primary"
            />
            <div>
              <Label htmlFor="bio" className="text-text-primary">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Tell us about yourself..."
                rows={4}
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
            <div>
              <Label htmlFor="voice_tagline" className="text-text-primary">Voice tagline</Label>
              <Input
                id="voice_tagline"
                value={formData.voice_tagline || ''}
                onChange={(e) => handleInputChange('voice_tagline', e.target.value.slice(0, 120))}
                maxLength={120}
                placeholder="One line under your name — e.g. “Warm tenor for narration & promos”"
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
              <p className="text-text-tertiary text-xs mt-1">
                {(formData.voice_tagline || '').length}/120 · shows beneath your name on your profile.
              </p>
            </div>
            <div>
              <Label htmlFor="location" className="text-text-primary">Location</Label>
              <Input
                id="location"
                value={formData.location || ''}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="City, State/Country"
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
          </div>
        );

      case 'skills':
        return (
          <div className="space-y-4">
            <div>
              <Label>Current Skills</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.skills?.map((skill: string, index: number) => (
                  <Badge key={index} variant="secondary" className="bg-accent-soft text-accent">
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-2 text-text-tertiary hover:text-text-primary"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="custom_skill" className="text-text-primary">Add Custom Skill</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="custom_skill"
                  value={customSkill}
                  onChange={(e) => setCustomSkill(e.target.value)}
                  placeholder="Enter a skill..."
                  onKeyPress={(e) => e.key === 'Enter' && addCustomSkill()}
                  className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
                />
                <Button type="button" onClick={addCustomSkill} disabled={!customSkill.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label>Current Specialties</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.specialties?.map((specialty: string, index: number) => (
                  <Badge key={index} variant="secondary" className="bg-accent-soft text-accent">
                    {specialty}
                    <button
                      type="button"
                      onClick={() => removeSpecialty(specialty)}
                      className="ml-2 text-text-tertiary hover:text-text-primary"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="custom_specialty" className="text-text-primary">Add Specialty</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="custom_specialty"
                  value={customSpecialty}
                  onChange={(e) => setCustomSpecialty(e.target.value)}
                  placeholder="Enter a specialty..."
                  onKeyPress={(e) => e.key === 'Enter' && addCustomSpecialty()}
                  className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
                />
                <Button type="button" onClick={addCustomSpecialty} disabled={!customSpecialty.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        );

      case 'talent-details': {
        const voiceTypes: string[] = Array.isArray(formData.voice_type) ? formData.voice_type : [];
        const unionStatus: string[] = Array.isArray(formData.union_status) ? formData.union_status : [];
        return (
          <div className="space-y-6">
            {/* Castable age range */}
            <div>
              <DsLabel>Castable age range</DsLabel>
              <p className="text-text-tertiary text-xs mb-2">
                The age range your voice can play — not your real age. 0–120; leave either blank.
              </p>
              <div className="flex items-center gap-2">
                <DsInput
                  type="number"
                  min={0}
                  max={120}
                  inputMode="numeric"
                  placeholder="Min"
                  aria-label="Castable age minimum"
                  value={formData.voice_age_min ?? ''}
                  onChange={(e) => handleInputChange('voice_age_min', e.target.value)}
                  className="w-24"
                />
                <span className="text-text-tertiary text-sm">to</span>
                <DsInput
                  type="number"
                  min={0}
                  max={120}
                  inputMode="numeric"
                  placeholder="Max"
                  aria-label="Castable age maximum"
                  value={formData.voice_age_max ?? ''}
                  onChange={(e) => handleInputChange('voice_age_max', e.target.value)}
                  className="w-24"
                />
              </div>
            </div>

            {/* Voice type (multi-select) */}
            <div>
              <DsLabel>Voice type</DsLabel>
              <p className="text-text-tertiary text-xs mb-2">
                Describes your voice, not your gender identity. Pick any that apply.
              </p>
              <div className="flex flex-wrap gap-2">
                {VOICE_TYPE_OPTIONS.map(([val, label]) => {
                  const on = voiceTypes.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleArrayValue('voice_type', val)}
                      className={cn(
                        'h-9 px-3.5 rounded-sm text-sm font-medium transition-colors',
                        on
                          ? 'bg-accent text-text-on-accent'
                          : 'bg-surface-3 text-text-secondary border border-border-default hover:bg-surface-2 hover:text-text-primary'
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Union status (multi-select) */}
            <div>
              <DsLabel>Union status</DsLabel>
              <p className="text-text-tertiary text-xs mb-2">
                Whether you&apos;re willing and able to work union, non-union, or both.
              </p>
              <div className="flex flex-wrap gap-2">
                {UNION_OPTIONS.map(([val, label]) => {
                  const on = unionStatus.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleArrayValue('union_status', val)}
                      className={cn(
                        'h-9 px-3.5 rounded-sm text-sm font-medium transition-colors',
                        on
                          ? 'bg-accent text-text-on-accent'
                          : 'bg-surface-3 text-text-secondary border border-border-default hover:bg-surface-2 hover:text-text-primary'
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Rates */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <DsLabel className="mb-0.5">Open to unpaid / deferred work</DsLabel>
                  <p className="text-text-tertiary text-xs">
                    Show scouts you&apos;ll consider unpaid or deferred projects.
                  </p>
                </div>
                <DsSwitch
                  checked={!!formData.open_to_unpaid}
                  onCheckedChange={(v) => handleInputChange('open_to_unpaid', v)}
                  aria-label="Open to unpaid or deferred work"
                />
              </div>
              <div>
                <DsLabel>Minimum rate (optional)</DsLabel>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary text-sm pointer-events-none">
                      $
                    </span>
                    <DsInput
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      placeholder="0.00"
                      aria-label="Minimum rate in dollars"
                      value={rateAmount}
                      onChange={(e) => setRateAmount(e.target.value)}
                      className="w-32 pl-6"
                    />
                  </div>
                  <DsSelect
                    aria-label="Rate unit"
                    value={formData.rate_unit || 'per_hour'}
                    onChange={(e) => handleInputChange('rate_unit', e.target.value)}
                    className="w-48"
                  >
                    {RATE_UNIT_OPTIONS.map(([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    ))}
                  </DsSelect>
                </div>
                <p className="text-text-tertiary text-xs mt-1">
                  Leave blank if you&apos;d rather not list a rate.
                </p>
              </div>
            </div>
          </div>
        );
      }

      case 'resume':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="resume_upload">Upload Resume</Label>
              <Input
                id="resume_upload"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const supabase = createClient();
                    const path = `resumes/${userId}/${Date.now()}-${file.name}`;
                    
                    const { error: uploadError } = await supabase.storage
                      .from('user-assets')
                      .upload(path, file, { upsert: true });
                    
                    if (uploadError) {
                      toast({
                        title: "Upload Error",
                        description: uploadError.message,
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    const { data: { publicUrl } } = supabase.storage
                      .from('user-assets')
                      .getPublicUrl(path);
                    
                    handleInputChange('resume_url', publicUrl);
                  }
                }}
              />
            </div>
            {formData.resume_url && (
              <div className="p-3 bg-surface-3 rounded-lg">
                <p className="text-status-online text-sm">Resume uploaded successfully!</p>
              </div>
            )}
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@example.com"
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <Input
                id="phone"
                value={formData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
          </div>
        );

      case 'avatar':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="avatar_upload">Upload Profile Picture</Label>
              <Input
                id="avatar_upload"
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      console.log('Starting avatar upload for user:', userId);
                      const supabase = createClient();
                      const path = `avatars/${userId}/${Date.now()}-${file.name}`;
                      
                      console.log('Uploading to path:', path);
                      const { error: uploadError } = await supabase.storage
                        .from('user-assets')
                        .upload(path, file, { upsert: true });
                      
                      if (uploadError) {
                        console.error('Upload error:', uploadError);
                        toast({
                          title: "Upload Error",
                          description: uploadError.message,
                          variant: "destructive",
                        });
                        return;
                      }
                      
                      const { data: { publicUrl } } = supabase.storage
                        .from('user-assets')
                        .getPublicUrl(path);
                      
                      console.log('Upload successful, public URL:', publicUrl);
                      handleInputChange('avatar_url', publicUrl);
                      
                      // Also save to the database immediately
                      const { error: updateError } = await supabase
                        .from('profiles')
                        .update({ avatar_url: publicUrl })
                        .eq('id', userId);
                      
                      if (updateError) {
                        console.error('Error updating avatar in database:', updateError);
                        toast({
                          title: "Upload Warning",
                          description: "Image uploaded but failed to save to profile. Please try again.",
                          variant: "destructive",
                        });
                      } else {
                        // Also save to media_files table for tracking
                        const { error: mediaError } = await supabase
                          .from('media_files')
                          .insert({
                            user_id: userId,
                            file_type: 'image',
                            file_url: publicUrl,
                            file_name: file.name,
                            file_size: file.size,
                            mime_type: file.type,
                            label: 'Profile Picture',
                            visibility: 'public'
                          });
                        
                        if (mediaError) {
                          console.error('Error saving media metadata:', mediaError);
                        }
                        
                        toast({
                          title: "Profile Picture Updated",
                          description: "Your profile picture has been updated successfully.",
                        });
                        
                        // Refresh the profile data
                        onRefresh?.();
                      }
                    } catch (error) {
                      console.error('Profile picture upload error:', error);
                      toast({
                        title: "Upload Error",
                        description: "Failed to upload profile picture. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }
                }}
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
            {formData.avatar_url && (
              <div className="p-3 bg-surface-3 rounded-lg">
                <p className="text-status-online text-sm mb-2">Profile picture uploaded successfully!</p>
                <div className="relative w-20 h-20 rounded-full overflow-hidden">
                  <Image
                    src={formData.avatar_url}
                    alt="New Profile Picture"
                    fill
                    sizes="80px"
                    style={{ objectFit: 'cover' }}
                    unoptimized
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'background':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="background_upload">Upload Background Image</Label>
              <Input
                id="background_upload"
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const supabase = createClient();
                    const path = `backgrounds/${userId}/${Date.now()}-${file.name}`;
                    
                    const { error: uploadError } = await supabase.storage
                      .from('user-assets')
                      .upload(path, file, { upsert: true });
                    
                    if (uploadError) {
                      toast({
                        title: "Upload Error",
                        description: uploadError.message,
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    const { data: { publicUrl } } = supabase.storage
                      .from('user-assets')
                      .getPublicUrl(path);
                    
                    handleInputChange('banner_url', publicUrl);
                  }
                }}
                className="bg-surface-3 text-text-primary border-border-default placeholder:text-text-tertiary focus:border-accent"
              />
            </div>
            {formData.banner_url && (
              <div className="p-3 bg-surface-3 rounded-lg">
                <p className="text-status-online text-sm mb-2">Background uploaded successfully!</p>
                <div className="relative w-full h-32 rounded-lg overflow-hidden">
                  <Image
                    src={formData.banner_url}
                    alt="New Background"
                    fill
                    sizes="(max-width: 768px) 100vw, 768px"
                    style={{ objectFit: 'cover' }}
                    unoptimized
                  />
                </div>
              </div>
            )}
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleInputChange('banner_url', '')}
                className="border-border-default text-text-secondary hover:bg-surface-2"
              >
                Remove Background
              </Button>
            </div>
          </div>
        );

      case 'photos':
        return (
          <div className="space-y-4">
            <p className="text-text-secondary">
              Portfolio photos are managed directly on your profile page. Click the empty boxes to upload photos and drag them to reorder.
            </p>
          </div>
        );

      case 'demo-reels': {
        // Upload-only step. media_files INSERT is deferred to Save Changes
        // so the user gets a real preview BEFORE committing. Cancel /
        // Replace / Remove all delete the orphaned storage object.
        const isCoarsePointer =
          typeof window !== 'undefined' &&
          (window.matchMedia?.('(pointer: coarse)').matches || window.innerWidth < 640);

        const handleReelPick = async (file: File, reelType: ReelKind) => {
          // Defensive: Drive picks on Android sometimes return zero-byte
          // File objects, wrong/empty mime types, or oversized files.
          // Fail fast with a clear message instead of bubbling up
          // "Failed to fetch" from the network layer.
          const preflight = preflightReelFile(file, reelType);
          if (!preflight.ok) {
            toast({
              title:
                preflight.code === 'empty'
                  ? 'Could Not Read File'
                  : preflight.code === 'too-large'
                  ? 'File Too Large'
                  : 'Unsupported File Type',
              description: preflight.message,
              variant: 'destructive',
            });
            return;
          }

          const setBusy = reelType === 'audio' ? setAudioBusy : setVideoBusy;
          const setProgress = reelType === 'audio' ? setAudioProgress : setVideoProgress;
          const setPending = reelType === 'audio' ? setPendingAudio : setPendingVideo;
          const existing = reelType === 'audio' ? pendingAudio : pendingVideo;
          const abortRef = reelType === 'audio' ? audioAbortRef : videoAbortRef;

          const controller = new AbortController();
          abortRef.current = controller;
          setBusy(true);
          setProgress(0);

          const supabase = createClient();
          let uploadedPath: string | null = null;
          try {
            // If swapping (Replace), delete the prior orphan storage object first.
            if (existing) {
              await supabase.storage.from('user-assets').remove([existing.storagePath]);
            }

            const sanitize = (name: string) =>
              name
                .normalize('NFKD')
                .replace(/[^\w.\-]+/g, '_')
                .replace(/_+/g, '_')
                .replace(/^[._-]+|[._-]+$/g, '');
            const safeName = sanitize(file.name) || `${reelType}.bin`;
            const uuid =
              typeof crypto !== 'undefined' && 'randomUUID' in crypto
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            const path = `reels/${userId}/${reelType}/${uuid}-${safeName}`;

            await storageUploadWithProgress({
              supabase,
              bucket: 'user-assets',
              path,
              file,
              contentType: file.type || undefined,
              cacheControl: '3600',
              upsert: false,
              signal: controller.signal,
              onProgress: ({ pct }) => setProgress(pct),
            });
            uploadedPath = path;

            const {
              data: { publicUrl },
            } = supabase.storage.from('user-assets').getPublicUrl(path);
            setPending({ storagePath: path, publicUrl, file });
          } catch (error: unknown) {
            const isAbort = error instanceof StorageUploadError && error.isAbort;
            const message = describeUploadError(error);

            // Best-effort orphan cleanup if the abort/network failure
            // happened mid-upload after partial bytes landed.
            if (uploadedPath) {
              try {
                await supabase.storage.from('user-assets').remove([uploadedPath]);
              } catch {
                // Non-fatal; the orphan-sweep job will reap eventually.
              }
            }

            reportUploadError({
              userId,
              reelType,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              status: error instanceof StorageUploadError ? error.status : 0,
              errorMessage: message,
              isAbort,
              isNetwork: error instanceof StorageUploadError ? error.isNetwork : false,
            });

            toast({
              title: isAbort ? 'Upload Cancelled' : 'Upload Error',
              description: message,
              variant: isAbort ? 'default' : 'destructive',
            });
          } finally {
            abortRef.current = null;
            setBusy(false);
            setProgress(0);
          }
        };

        const handleReelCancel = (reelType: ReelKind) => {
          const abortRef = reelType === 'audio' ? audioAbortRef : videoAbortRef;
          abortRef.current?.abort();
        };

        const handleReelRemove = async (reelType: ReelKind) => {
          const existing = reelType === 'audio' ? pendingAudio : pendingVideo;
          const setPending = reelType === 'audio' ? setPendingAudio : setPendingVideo;
          if (!existing) return;
          try {
            const supabase = createClient();
            await supabase.storage.from('user-assets').remove([existing.storagePath]);
          } catch (err) {
            console.warn('Failed to clean orphan storage object:', err);
          }
          setPending(null);
        };

        const renderUploadProgress = (kind: ReelKind, pct: number) => (
          <div className="mt-3 space-y-2" aria-live="polite">
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span>Uploading… {pct}%</span>
              <button
                type="button"
                onClick={() => handleReelCancel(kind)}
                className="text-status-busy hover:opacity-80 underline"
              >
                Cancel
              </button>
            </div>
            <div className="w-full h-2 bg-surface-3 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-200"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );

        return (
          <div className="space-y-6">
            <div>
              <Label className="text-text-primary text-lg mb-4 block">Upload Demo Reels</Label>
              <p className="text-text-tertiary text-sm mb-2">
                Upload audio or video demo reels to showcase your work. Supported formats: Audio (.wav, .mp3, .m4a, .aac, .ogg, .flac) | Video (.mp4, .mov, .avi, .webm, .mkv, .m4v)
              </p>
              <p className="text-text-tertiary text-xs mb-4">Maximum file size: 100 MB.</p>
              {isCoarsePointer && (
                <div className="mb-4 p-3 rounded-lg bg-surface-3 border border-border-default text-text-secondary text-xs">
                  Tip: For best results, save the file to your device before uploading. Google Drive picks may fail on slow connections.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Audio Upload */}
                <div className="rounded-lg border-2 border-dashed border-border-default hover:border-accent transition-colors p-4">
                  {pendingAudio ? (
                    <div className="space-y-3">
                      <AudioReelPlayer
                        src={pendingAudio.publicUrl}
                        title={pendingAudio.file.name}
                        subtitle={`${pendingAudio.file.type || 'audio'} · ${formatFileSize(pendingAudio.file.size)} · attached, save to keep`}
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-border-default text-text-secondary hover:bg-surface-2"
                          onClick={() => document.getElementById('audio_reel_upload')?.click()}
                          disabled={audioBusy}
                        >
                          {audioBusy ? 'Uploading…' : 'Replace'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-border-default text-status-busy hover:bg-surface-2"
                          onClick={() => handleReelRemove('audio')}
                          disabled={audioBusy}
                        >
                          Remove
                        </Button>
                      </div>
                      {audioBusy && renderUploadProgress('audio', audioProgress)}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-3 py-2 text-center">
                      <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                      </div>
                      <div>
                        <Label htmlFor="audio_reel_upload" className="text-text-primary font-medium cursor-pointer">
                          Upload Audio Reel
                        </Label>
                        <p className="text-text-tertiary text-xs mt-1">.wav, .mp3, .m4a, .aac, .ogg, .flac</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-border-default text-text-secondary hover:bg-surface-2"
                        onClick={() => document.getElementById('audio_reel_upload')?.click()}
                        disabled={audioBusy}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {audioBusy ? 'Uploading…' : 'Choose Audio File'}
                      </Button>
                      {audioBusy && (
                        <div className="w-full">{renderUploadProgress('audio', audioProgress)}</div>
                      )}
                    </div>
                  )}
                  <Input
                    id="audio_reel_upload"
                    type="file"
                    accept=".wav,.mp3,.m4a,.aac,.ogg,.flac,audio/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        await handleReelPick(file, 'audio');
                        e.target.value = '';
                      }
                    }}
                    className="hidden"
                  />
                </div>

                {/* Video Upload */}
                <div className="rounded-lg border-2 border-dashed border-border-default hover:border-accent transition-colors p-4">
                  {pendingVideo ? (
                    <div className="space-y-3">
                      <VideoReelPlayer
                        src={pendingVideo.publicUrl}
                        title={pendingVideo.file.name}
                        subtitle={`${pendingVideo.file.type || 'video'} · ${formatFileSize(pendingVideo.file.size)} · attached, save to keep`}
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-border-default text-text-secondary hover:bg-surface-2"
                          onClick={() => document.getElementById('video_reel_upload')?.click()}
                          disabled={videoBusy}
                        >
                          {videoBusy ? 'Uploading…' : 'Replace'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-border-default text-status-busy hover:bg-surface-2"
                          onClick={() => handleReelRemove('video')}
                          disabled={videoBusy}
                        >
                          Remove
                        </Button>
                      </div>
                      {videoBusy && renderUploadProgress('video', videoProgress)}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-3 py-2 text-center">
                      <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <Label htmlFor="video_reel_upload" className="text-text-primary font-medium cursor-pointer">
                          Upload Video Reel
                        </Label>
                        <p className="text-text-tertiary text-xs mt-1">.mp4, .mov, .avi, .webm, .mkv, .m4v</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-border-default text-text-secondary hover:bg-surface-2"
                        onClick={() => document.getElementById('video_reel_upload')?.click()}
                        disabled={videoBusy}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {videoBusy ? 'Uploading…' : 'Choose Video File'}
                      </Button>
                      {videoBusy && (
                        <div className="w-full">{renderUploadProgress('video', videoProgress)}</div>
                      )}
                    </div>
                  )}
                  <Input
                    id="video_reel_upload"
                    type="file"
                    accept=".mp4,.mov,.avi,.webm,.mkv,.m4v,video/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        await handleReelPick(file, 'video');
                        e.target.value = '';
                      }
                    }}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }

      default:
        return <div>Edit form for {sectionId}</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-surface-2 border-border-subtle">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-text-primary">
            Edit {sectionId.charAt(0).toUpperCase() + sectionId.slice(1).replace('-', ' ')}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-text-tertiary hover:text-text-primary"
          >
            <CloseIcon className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {renderSectionContent()}
          
          <div className="flex justify-end space-x-3 pt-4 border-t border-border-subtle">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-border-default text-text-secondary hover:bg-surface-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-accent hover:bg-accent-hover text-text-on-accent"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
